import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { HorizontalFilterBar } from '../../Listings/Filters/HorizontalFilterBar';
import { AddressSearch } from '../../Filters/AddressSearch';
import { venueTypes } from '../../../lib/constants/venueTypes';
import { amenities } from '../../../lib/constants/amenities';
import { europeanCountries } from '../../../lib/constants/countries';

interface Filters {
  venueTypes: string[];
  amenities: string[];
  capacity: string;
  countries: string[];
  nearMe: boolean;
  forRent: boolean;
}

interface VenueFiltersProps {
  filters: Filters;
  onFilterChange: (filterType: keyof Filters, values: string[] | boolean) => void;
  locationLoading?: boolean;
  onAddressSelect?: (latitude: number, longitude: number, address: string) => void;
  showExpanded?: boolean;
}

export function VenueFilters({ filters, onFilterChange, locationLoading, onAddressSelect, showExpanded = true }: VenueFiltersProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(showExpanded);

  const capacityRanges = [
    { value: 'under_20', label: t('filters.capacityUnder20') },
    { value: '20_50', label: t('filters.capacity20to50') },
    { value: '50_100', label: t('filters.capacity50to100') },
    { value: 'over_100', label: t('filters.capacityOver100') }
  ];
  const hasActiveFilters = filters.venueTypes.length > 0 ||
                          filters.amenities.length > 0 ||
                          filters.capacity !== '' ||
                          filters.countries.length > 0 ||
                          filters.nearMe ||
                          filters.forRent;

  const clearAllFilters = () => {
    onFilterChange('venueTypes', []);
    onFilterChange('amenities', []);
    onFilterChange('capacity', ['']);
    onFilterChange('countries', []);
    onFilterChange('nearMe', false);
    onFilterChange('forRent', false);
  };

  const filterGroups = [
    {
      label: t('filters.venueType'),
      options: venueTypes,
      selectedValues: filters.venueTypes,
      onChange: (values: string[]) => onFilterChange('venueTypes', values),
      multiSelect: true
    },
    {
      label: t('filters.country'),
      options: europeanCountries,
      selectedValues: filters.countries,
      onChange: (values: string[]) => onFilterChange('countries', values),
      multiSelect: true
    },
    {
      label: t('filters.amenities'),
      options: amenities,
      selectedValues: filters.amenities,
      onChange: (values: string[]) => onFilterChange('amenities', values),
      multiSelect: true
    },
    {
      label: t('filters.capacity'),
      options: capacityRanges,
      selectedValues: filters.capacity ? [filters.capacity] : [],
      onChange: (values: string[]) => onFilterChange('capacity', values),
      multiSelect: false
    }
  ];

  return (
    <div className="border-b border-gray-200 w-full">
      <div className="px-3 sm:px-4 lg:px-6 max-w-page mx-auto">
        {/* Mobile: compact row with Near Me + Filters button */}
        <div className="lg:hidden flex items-center gap-2 py-2">
          <button
            onClick={() => onFilterChange('nearMe', !filters.nearMe)}
            disabled={locationLoading}
            className={`flex-shrink-0 h-9 px-4 rounded-full gap-2 inline-flex items-center text-sm font-medium transition-colors active:scale-95 ${
              filters.nearMe
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
            } ${locationLoading ? 'opacity-50 cursor-wait' : ''}`}
          >
            {locationLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
            <span>{t('filters.nearMe')}</span>
          </button>
          <button
            onClick={() => onFilterChange('forRent', !filters.forRent)}
            className={`flex-shrink-0 h-9 px-4 rounded-full gap-2 inline-flex items-center text-sm font-medium transition-colors active:scale-95 ${
              filters.forRent
                ? 'bg-[#8DA847] text-white hover:bg-[#7A9339]'
                : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
            }`}
          >
            <span>{t('filters.forRent')}</span>
          </button>
          <HorizontalFilterBar
            filterGroups={filterGroups}
            onClearAll={clearAllFilters}
            hasActiveFilters={hasActiveFilters}
            inline={true}
          />
        </div>

        {/* Desktop: full horizontal row */}
        <div className="hidden lg:block">
          <div className="w-full overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-1.5 sm:gap-2 py-2 min-w-max">
              <button
                onClick={() => onFilterChange('nearMe', !filters.nearMe)}
                disabled={locationLoading}
                className={`flex-shrink-0 h-9 px-4 rounded-full gap-2 inline-flex items-center text-sm font-medium transition-colors ${
                  filters.nearMe
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
                } ${locationLoading ? 'opacity-50 cursor-wait' : ''}`}
              >
                {locationLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                <span>{t('filters.nearMe')}</span>
              </button>

              <button
                onClick={() => onFilterChange('forRent', !filters.forRent)}
                className={`flex-shrink-0 h-9 px-4 rounded-full gap-2 inline-flex items-center text-sm font-medium transition-colors ${
                  filters.forRent
                    ? 'bg-[#8DA847] text-white hover:bg-[#7A9339]'
                    : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <span>{t('filters.forRent')}</span>
              </button>

              <HorizontalFilterBar
                filterGroups={filterGroups}
                onClearAll={clearAllFilters}
                hasActiveFilters={hasActiveFilters}
                inline={true}
              />

              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex-shrink-0 h-9 px-3 rounded-full gap-2 inline-flex items-center text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('filters.less')}</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('filters.more')}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {isExpanded && onAddressSelect && (
            <div className="pt-4 pb-4 border-t border-gray-200 mt-2">
              <div className="max-w-md">
                <AddressSearch onAddressSelect={onAddressSelect} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
